import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';
import { FiBriefcase } from 'react-icons/fi';

const StudentPlacementProfile = () => {
    const [profile, setProfile] = useState({
        enrollmentNo: '', branch: '', resumeLink: '', tenthPercentage: '', twelfthPercentage: '', cgpa: '', activeBacklogs: 0, githubLink: '', linkedinLink: ''
    });
    const [drives, setDrives] = useState([]);
    const [applications, setApplications] = useState([]);
    const [trainings, setTrainings] = useState([]);

    useEffect(() => {
        fetchProfile();
        fetchEligibleDrives();
        fetchMyApplications();
        fetchTrainings();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${baseApiURL()}/student/placement/profile`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success && res.data.profile) {
                setProfile(res.data.profile);
            }
        } catch (error) {
            console.error('No profile found or error fetching profile');
        }
    };

    const fetchEligibleDrives = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${baseApiURL()}/placement/drives/get`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                setDrives(res.data.drives);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchMyApplications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${baseApiURL()}/placement/applications/student/my-applications`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                setApplications(res.data.applications);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTrainings = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${baseApiURL()}/placement/training/get`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                setTrainings(res.data.trainings);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleApply = async (driveId) => {
        try {
            const token = localStorage.getItem('token');
            let studentId = '';
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const decoded = JSON.parse(window.atob(base64));
                studentId = decoded.id;
            } catch (e) {
                console.error("Token decoding error", e);
            }

            const res = await axios.post(
                `${baseApiURL()}/placement/applications/add`,
                { drive: driveId, student: studentId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.data.success) {
                toast.success('Applied successfully!');
                fetchMyApplications();
            } else {
                toast.error(res.data.message || 'Failed to apply');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error applying to drive');
        }
    };

    return (
        <div className="w-full space-y-6">
            {/* 🌟 Header Banner */}
            <div className="bento-header-banner flex items-center justify-between">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2 text-slate-900 dark:text-white">
                        <FiBriefcase className="text-indigo-600 dark:text-indigo-400" />
                        <span>Placement Hub & Drive Applications</span>
                    </h1>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">Review active campus placements, submit drive applications, and track results</p>
                </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Profile Section */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-3">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white">My Placement Profile</h3>
                        <span className="text-xs bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 px-2.5 py-1 rounded-full font-bold border border-indigo-100 dark:border-indigo-500/20">
                            Synced from My Profile
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-xs">
                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">Enrollment No.</span>
                            <span className="font-bold text-slate-900 dark:text-white text-sm">{profile.enrollmentNo || "N/A"}</span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">Branch</span>
                            <span className="font-bold text-slate-900 dark:text-white text-sm">{profile.branch || "N/A"}</span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">10th Percentage</span>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400 text-sm">
                                {profile.tenthPercentage !== undefined && profile.tenthPercentage !== null && profile.tenthPercentage !== "" ? `${profile.tenthPercentage}%` : "N/A"}
                            </span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">12th Percentage</span>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400 text-sm">
                                {profile.twelfthPercentage !== undefined && profile.twelfthPercentage !== null && profile.twelfthPercentage !== "" ? `${profile.twelfthPercentage}%` : "N/A"}
                            </span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">Current CGPA</span>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400 text-sm">
                                {profile.cgpa !== undefined && profile.cgpa !== null && profile.cgpa !== "" ? profile.cgpa : "N/A"}
                            </span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block">Active Backlogs</span>
                            <span className={`font-bold text-sm ${profile.activeBacklogs > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                                {profile.activeBacklogs ?? 0}
                            </span>
                        </div>

                        <div className="col-span-2 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block mb-1">Resume Link</span>
                            {profile.resumeLink ? (
                                <a href={profile.resumeLink} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 font-bold text-xs underline truncate block">
                                    {profile.resumeLink}
                                </a>
                            ) : (
                                <span className="text-slate-400 font-medium">Not Uploaded (Add in My Profile)</span>
                            )}
                        </div>

                        <div className="col-span-2 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium block mb-1">LinkedIn Profile</span>
                            {profile.linkedinLink ? (
                                <a href={profile.linkedinLink} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 font-bold text-xs underline truncate block">
                                    {profile.linkedinLink}
                                </a>
                            ) : (
                                <span className="text-slate-400 font-medium">Not Added (Add in My Profile)</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Drives and Applications Section */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">Available Drives</h3>
                        <div className="space-y-3">
                            {drives.filter(d => d.status === 'Upcoming' || d.status === 'Ongoing').map(drive => {
                                const hasApplied = applications.some(app => app.drive._id === drive._id);
                                return (
                                    <div key={drive._id} className="border border-slate-200 dark:border-slate-800 p-4 rounded-xl bg-slate-50 dark:bg-slate-950 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-bold text-sm text-slate-900 dark:text-white">{drive.title} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">at {drive.company?.name}</span></h4>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Deadline: {new Date(drive.registrationDeadline).toLocaleDateString()}</p>
                                        </div>
                                        <button 
                                            onClick={() => handleApply(drive._id)}
                                            disabled={hasApplied}
                                            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${hasApplied ? 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-xs'}`}
                                        >
                                            {hasApplied ? 'Applied' : 'Apply Now'}
                                        </button>
                                    </div>
                                );
                            })}
                            {drives.length === 0 && <p className="text-slate-500 dark:text-slate-400 text-xs">No drives available right now.</p>}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">My Applications</h3>
                        <div className="space-y-3">
                            {applications.map(app => (
                                <div key={app._id} className="border border-slate-200 dark:border-slate-800 p-4 rounded-xl bg-slate-50 dark:bg-slate-950 flex justify-between items-center">
                                    <div>
                                        <h4 className="font-bold text-sm text-slate-900 dark:text-white">{app.drive?.title} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">at {app.drive?.company?.name || 'Company'}</span></h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Applied On: {new Date(app.createdAt).toLocaleDateString()}</p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold
                                        ${app.status === 'Applied' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/20' :
                                          app.status === 'Shortlisted' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/20' :
                                          app.status === 'Selected' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/20' :
                                          'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/20'}`}>
                                        {app.status}
                                    </span>
                                </div>
                            ))}
                            {applications.length === 0 && <p className="text-slate-500 dark:text-slate-400 text-xs">No active applications found.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentPlacementProfile;
